import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../common";

function Fld({ label, children }) {
  return <div><Label className="mb-1 block normal-case tracking-normal text-slate-600">{label}</Label>{children}</div>;
}

export default function EmployeeEditDialog({ open, onOpenChange, cv, locations, employeeId, onSaved }) {
  const emp = cv?.employee;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newAccountEmail, setNewAccountEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [acctBusy, setAcctBusy] = useState(false);

  useEffect(() => {
    if (open && emp) {
      setForm({
        name: emp.name || "", job_title: emp.job_title || "", location_id: emp.location_id || "",
        phone: emp.phone || "", photo_url: emp.photo_url || "", show_photo: emp.show_photo !== false,
        hire_date: emp.hire_date ? emp.hire_date.slice(0, 10) : "",
        id_number: emp.id_number || "",
        email: cv?.account?.email || "",
      });
    }
  }, [open, emp, cv]);

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 1024 * 1024) { toast.error("Photo must be under 1 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${employeeId}`, { ...form, hire_date: form.hire_date || null });
      toast.success("Profile updated");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const createAccount = async () => {
    if (!newAccountEmail.trim()) { toast.error("Enter an email for the login account"); return; }
    setAcctBusy(true);
    try {
      await api.post(`/employees/${employeeId}/account`, { email: newAccountEmail.trim() });
      toast.success("Login account created — a set-password invite was emailed");
      setForm((f) => ({ ...(f || {}), email: newAccountEmail.trim() }));
      setNewAccountEmail("");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAcctBusy(false); }
  };

  const setEmployeePassword = async () => {
    if (!newPassword || newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setAcctBusy(true);
    try {
      await api.post(`/employees/${employeeId}/account/password`, { password: newPassword });
      toast.success("Password updated — the employee's existing sessions were signed out");
      setNewPassword("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAcctBusy(false); }
  };

  const sendResetEmail = async () => {
    setAcctBusy(true);
    try {
      await api.post(`/employees/${employeeId}/account/password`, { send_email: true });
      toast.success("A set-password link was emailed to the employee");
    } catch (e) { toast.error(apiError(e)); }
    finally { setAcctBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit employee profile</DialogTitle></DialogHeader>
        {form && (
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-50">
                {form.photo_url ? <img src={form.photo_url} alt="" className="h-full w-full object-cover" data-testid="edit-photo-preview" /> : <span className="text-[10px] text-slate-400">No photo</span>}
              </div>
              <div className="flex-1">
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="edit-photo-label">
                  Upload photo<input type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="edit-photo-input" />
                </label>
                {form.photo_url && <button type="button" onClick={() => setForm({ ...form, photo_url: "" })} className="ml-2 text-sm font-semibold text-red-600 hover:underline" data-testid="edit-photo-remove">Remove</button>}
              </div>
            </div>
            <Fld label="Full name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></Fld>
            <Fld label="Job title"><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="edit-jobtitle" /></Fld>
            <Fld label="Phone number"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+27…" data-testid="edit-phone" /></Fld>
            <Fld label="ID / Passport number"><Input value={form.id_number} onChange={(e) => setForm({ ...form, id_number: e.target.value })} placeholder="e.g. 8801015800088" data-testid="edit-idnumber" /></Fld>
            <Fld label="Location">
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="edit-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Hire date"><Input type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} data-testid="edit-hiredate" /></Fld>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600" data-testid="edit-showphoto-row">
              <input type="checkbox" checked={form.show_photo} onChange={(e) => setForm({ ...form, show_photo: e.target.checked })} data-testid="edit-showphoto" />
              Show photo on the public feedback page
            </label>

            {cv?.account ? (
              <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid="account-manage">
                <Label className="normal-case tracking-normal text-slate-600">Login account</Label>
                <Fld label="Login email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" data-testid="edit-email" /></Fld>
                <div>
                  <Label className="mb-1 block normal-case tracking-normal text-slate-600">Set a new password</Label>
                  <div className="flex gap-2">
                    <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 8 characters" data-testid="edit-new-password" />
                    <Button type="button" variant="outline" onClick={setEmployeePassword} disabled={acctBusy} data-testid="set-password-btn">Set</Button>
                  </div>
                  <button type="button" onClick={sendResetEmail} disabled={acctBusy} className="mt-2 text-xs font-semibold text-indigo-600 hover:underline" data-testid="send-reset-btn">
                    …or email them a set-password link
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 rounded-lg border border-dashed border-indigo-200 bg-indigo-50/40 p-3" data-testid="account-create">
                <Label className="normal-case tracking-normal text-slate-600">Create a login account</Label>
                <p className="text-xs text-slate-500">Give this employee access to their Service CV. We'll email a secure set-password link. (No email? If their mobile number is saved on the profile, they can sign in with an SMS code from the login page.)</p>
                <div className="flex gap-2">
                  <Input type="email" value={newAccountEmail} onChange={(e) => setNewAccountEmail(e.target.value)} placeholder="name@company.com" data-testid="new-account-email" />
                  <Button type="button" onClick={createAccount} disabled={acctBusy} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="create-account-btn">Create &amp; invite</Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={saveEdit} disabled={saving || !form?.name} data-testid="save-profile" className="bg-[#4338CA] hover:bg-[#3730A3]">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
